
import throttle from 'lodash.throttle';
import moment from 'moment';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Input,
  Button,
  Image,
  List,
  Label,
  Container,
  Segment,
  Icon,
  Header,
  Divider, // Added Divider for visual separation
} from 'semantic-ui-react';
import { useQuery, gql, useMutation } from '@apollo/client';
import { useCallback, useState, useEffect } from 'react';

// Local imports
import { formatDate } from '../../dates';
import { ResponsiveContainer } from '../ResponsiveContainer';
import { InterimStates } from './InterimStates';
import { PersonCourseAttendance } from './PersonCourseAttendance';
import { PersonTags } from './PersonTags';
import { PermissionEditor } from './PermissionEditor';
import { useIsAdmin } from '../../hooks/isAdmin';

// --- GraphQL Queries and Mutations ---

const GET_PERSON = gql`
  query User($personId: Int!) {
    user(personId: $personId) {
      id
      photoUrl
      currentCourseAttendances {
        attendance {
            id
            status
        }
        courseId
        course {
            id
            name
            SKU
        }
      }
      isArchived
      uploadURLSmall
      scriptName
      canEditPhone
      phone
      haveRegistrationDocument
      registrationDocumentUrl
      preferredName
      identifier
      name
      dob
      tags {
        id
        name
      }
      address
      attendancePercentage
      truency
      picturePermission
      coursesTeaching {
        id
        name
        SKU
      }
      courses {
        id
        name
        SKU
      }
    }
  }
`;

const UPDATE_USER_FIELD = gql`
  mutation UpdateUserField($userId: Int, $value: String, $field: String) {
    updateUserField(userId: $userId, value: $value, field: $field) {
      id
    }
  }
`;

// --- Profile Component ---

const Profile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAdmin = useIsAdmin();

  // State for editable fields
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState('');
  const [scriptName, setScriptName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');

  // State for photo upload
  const [file, setFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showLargePhoto, setShowLargePhoto] = useState(false);

  // GraphQL hooks
  const { loading, error, data, refetch } = useQuery(GET_PERSON, {
    variables: { personId: Number(id) },
    skip: !id || isNaN(Number(id)),
  });

  const [updateUser] = useMutation(UPDATE_USER_FIELD);

  // Effect to initialize editable fields when data loads or changes
  useEffect(() => {
    if (data?.user) {
      setName(data.user.name || '');
      setScriptName(data.user.scriptName || '');
      setAddress(data.user.address || '');
      setPhone(data.user.phone || '');
    }
  }, [data]);

  // --- Throttled Update Functions ---

  const CreateThrottledUpdater = (field: string) =>
    useCallback(
      throttle(
        async (value: string) => {
          await updateUser({
            variables: {
              userId: Number(id),
              value,
              field,
            },
          });
          refetch();
        },
        500,
        { leading: false, trailing: true }
      ),
      [updateUser, id, refetch]
    );

  const updateName = CreateThrottledUpdater('name');
  const updateScriptName = CreateThrottledUpdater('scriptName');
  const updatePhone = CreateThrottledUpdater('phone');
  const updateAddress = CreateThrottledUpdater('address');

  const archiveUser = useCallback(
    throttle(
      async () => {
        if (window.confirm('Are you sure you want to archive this person?')) {
          await updateUser({
            variables: {
              userId: Number(id),
              value: 'now',
              field: 'archivedAt',
            },
          });
          navigate('/people');
        }
      },
      500,
      { leading: false, trailing: true }
    ),
    [updateUser, id, navigate]
  );

  // --- File Upload Logic ---

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const uploadProfilePhoto = async () => {
    if (!file || !data?.user?.uploadURLSmall) {
      console.error('No file selected or upload URL missing.');
      return;
    }
    setUploadingFile(true);
    try {
      await fetch(data.user.uploadURLSmall, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'application/octet-stream',
          'Content-Length': file.size.toString(),
        },
        referrer: 'no-referrer',
      });
      console.log('File uploaded successfully');
      setFile(null);
      refetch();
    } catch (uploadError) {
      console.error('Upload failed:', uploadError);
    } finally {
      setUploadingFile(false);
    }
  };

  // --- Render Logic ---

  if (loading || error || !data?.user) {
    return (
      <InterimStates
        loading={loading}
        error={error}
        label="person's details"
      />
    );
  }

  const { user } = data;
  const canEdit = editMode && isAdmin;

  return (
    <ResponsiveContainer>
      <Segment
        style={{ padding: '2em 0em', backgroundColor: '#f5f5f5' }} // Light background for the overall segment
        vertical
      >
        <Container style={{ maxWidth: '800px' }}>
          {/* Back Button and Edit Mode Toggle */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1em',
            }}
          >
            <Icon
              name="arrow left"
              size="large"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(-1)}
            />
            {(isAdmin || user.canEditPhone) && !user.isArchived && (
              <Button
                basic
                color="blue"
                onClick={() => {
                  if (!editMode) {
                    setName(user.name || '');
                    setAddress(user.address || '');
                    setPhone(user.phone || '');
                    setScriptName(user.scriptName || '');
                  }
                  setEditMode(!editMode);
                }}
              >
                <Icon name="edit" />
                {editMode ? 'Exit Edit Mode' : 'Edit Profile'}
              </Button>
            )}
          </div>

          {user.isArchived && (
            <Label color="red" size="large" style={{ marginBottom: '1em' }}>
              Archived Profile
            </Label>
          )}

          {/* --- Profile Header Section --- */}
          <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              {/* Photo Area */}
              <div style={{ marginRight: '2em' }}>
                {showLargePhoto ? (
                  <Container textAlign="center">
                    <Image
                      src={user.photoUrl}
                      size="large"
                      circular // Circular for profile photos
                      style={{
                        width: '180px',
                        height: '180px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        border: '2px solid #ddd',
                      }}
                      onClick={() => setShowLargePhoto(false)}
                    />
                    {canEdit && (
                      <div style={{ marginTop: '1em' }}>
                        {uploadingFile ? (
                          <Label basic loading>
                            Uploading...
                          </Label>
                        ) : (
                          <Label basic>Click photo to minimize</Label>
                        )}
                        <input type="file" onChange={handleFileChange} />
                        <Button
                          compact
                          primary
                          size="tiny"
                          onClick={uploadProfilePhoto}
                          disabled={!file}
                          style={{ marginTop: '0.5em' }}
                        >
                          Upload New Photo
                        </Button>
                      </div>
                    )}
                  </Container>
                ) : (
                  <>
                    <Image
                      src={user.photoUrl}
                      size="tiny"
                      circular // Circular for profile photos
                      style={{
                        width: '120px',
                        height: '120px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        border: '2px solid #ddd',
                      }}
                      onClick={() => setShowLargePhoto(true)}
                    />
                    {canEdit && (
                      <div style={{ marginTop: '0.5em' }}>
                        {uploadingFile ? (
                          <Label basic loading>
                            Uploading...
                          </Label>
                        ) : (
                          <Label basic>Click photo to enlarge</Label>
                        )}
                        <input
                          type="file"
                          onChange={handleFileChange}
                          style={{ width: '100%' }}
                        />
                        <Button
                          compact
                          primary
                          size="tiny"
                          onClick={uploadProfilePhoto}
                          disabled={!file}
                          style={{ marginTop: '0.5em' }}
                        >
                          Upload Photo
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Name and Basic Info */}
              <div>
                <Header as="h1" style={{ marginBottom: '0.2em' }}>
                  {canEdit ? (
                    <Input
                      fluid
                      placeholder="Full Name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        updateName(e.target.value);
                      }}
                    />
                  ) : (
                    user.name
                  )}
                </Header>

                {(user.scriptName || user.preferredName) && (
                  <Header as="h3" color="grey" style={{ marginTop: '0' }}>
                    {canEdit ? (
                      <Input
                        fluid
                        placeholder="Script Name (e.g., Arabic, Chinese)"
                        value={scriptName}
                        onChange={(e) => {
                          setScriptName(e.target.value);
                          updateScriptName(e.target.value);
                        }}
                      />
                    ) : (
                      <>
                        {user.scriptName}{' '}
                        {user.preferredName && `(${user.preferredName})`}
                      </>
                    )}
                  </Header>
                )}
                <p style={{ color: '#555', fontSize: '1.1em' }}>
                  <Icon name="id card outline" /> Identifier: {user.identifier}
                </p>
              </div>
            </div>
          </Segment>

          {/* --- About Section --- */}
          <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
            <Header as="h2">
              <Icon name="info circle" /> About
            </Header>
            <Divider />
            <List relaxed divided>
              <List.Item>
                <Label horizontal style={{ minWidth: '120px' }}>
                  <Icon name="home" /> Address
                </Label>
                {canEdit ? (
                  <Input
                    type="text"
                    fluid
                    value={address}
                    onChange={(e) => {
                      setAddress(e.target.value);
                      updateAddress(e.target.value);
                    }}
                  />
                ) : (
                  user.address || 'N/A'
                )}
              </List.Item>
              <List.Item>
                <Label horizontal style={{ minWidth: '120px' }}>
                  <Icon name="phone" /> Phone
                </Label>
                {editMode && (user.canEditPhone || isAdmin) ? (
                  <Input
                    type="text"
                    fluid
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      updatePhone(e.target.value);
                    }}
                  />
                ) : (
                  user.phone && <a href={`tel:${user.phone}`}>{user.phone}</a>
                )}
              </List.Item>
              <List.Item>
                <Label horizontal style={{ minWidth: '120px' }}>
                  <Icon name="birthday" /> Date of Birth
                </Label>
                {formatDate(user.dob)}
              </List.Item>
              <List.Item>
                <Label horizontal style={{ minWidth: '120px' }}>
                  <Icon name="calendar outline" /> Age
                </Label>
                {moment().diff(moment(Number(user.dob)), 'years')} years,{' '}
                {moment().diff(moment(Number(user.dob)), 'months') % 12} months
              </List.Item>
              <List.Item>
                <Label horizontal style={{ minWidth: '120px' }}>
                  <Icon name="check" /> Attendance
                </Label>
                {Math.round(user.attendancePercentage * 100)}%
              </List.Item>
              <List.Item>
                <Label horizontal style={{ minWidth: '120px' }}>
                  <Icon name="camera" /> Picture Permission
                </Label>
                {user.picturePermission ? 'Allowed' : 'Not Allowed'}
              </List.Item>
              <List.Item>
                <Label horizontal style={{ minWidth: '120px' }}>
                  <Icon name="bed" /> Truancy
                </Label>
                {user.truency}
              </List.Item>
              <List.Item disabled={!isAdmin}>
                <Label horizontal style={{ minWidth: '120px' }}>
                  <Icon name="file" /> Reg. Document
                </Label>
                {user.haveRegistrationDocument ? 'Yes ' : 'No '}
                {isAdmin && user.registrationDocumentUrl && (
                  <a
                    target="_blank"
                    href={user.registrationDocumentUrl}
                    rel="noopener noreferrer"
                  >
                    - View
                  </a>
                )}
              </List.Item>
            </List>
          </Segment>

          {/* --- Tags Section (if in edit mode) --- */}
          {canEdit && (
            <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
              <Header as="h2">
                <Icon name="tags" /> Profile Tags
              </Header>
              <Divider />
              <PersonTags
                tags={user.tags}
                refetchPerson={() =>
                  refetch({ variables: { personId: Number(id) } })
                }
              />
            </Segment>
          )}

          {/* --- Current Block Attendance Section --- */}
          <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
            <Header as="h2">
                <Icon name="calendar check outline" /> Current Block Attendance
            </Header>
            <Divider />
            {user.currentCourseAttendances.length === 0 ? (
              <p>Not currently enrolled in any classes.</p>
            ) : (
              <List divided relaxed>
                  {user.currentCourseAttendances.filter(({ course }: any) => {
                    return course.SKU.endsWith('JUL2025');
                })
                  .map(({ course, attendance }: any) => (
                  <List.Item key={course.id}>
                    <List.Content>
                      <List.Header as={Link} to={`/courses/${course.id}`}>
                        {course.name}
                      </List.Header>
                      <List.Description>
                      {course.SKU} <br/>
                      Attendance: {attendance.reduce((acc: number, a: any) => acc + (a.status === 'PRESENT' ? 1 : 0), 0)}/{attendance.length} <br/>
                         {attendance.map((a: any) => (
                            <span key={a.id} style={{ marginRight: '0.5em' }}>
                                {a.status === 'ABSENT' ? '❌' : a.status === 'PRESENT' ? '✅' : '❓'}
                            </span>
                         ))}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                ))}
                </List>
            )}
          </Segment>

          {/* --- Classes Section --- */}
          <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
            <Header as="h2">
              <Icon name="book" /> Past Classes
            </Header>
            <Divider />
            {user.courses
                 .length === 0 ? (
              <p>Not currently enrolled in any classes.</p>
            ) : (
              <List divided relaxed>
                {Object.entries(
                  user.courses
                 .reduce((acc: any, c: any) => {
                    const sku = c.SKU.slice(-7);
                    if (!acc[sku]) {
                      acc[sku] = [];
                    }
                    acc[sku].push(c);
                    return acc;
                  }, {})
                )
                  .sort(
                    ([skuA], [skuB]) =>
                      Number(skuA.slice(-4)) - Number(skuB.slice(-4))
                  ) // Sort accending by year/SKU
                  .map(([sku, courses]: [string, any]) => (
                    <List.Item key={sku}>
                      <Header as="h4" style={{ marginBottom: '0.5em' }}>
                        {sku}
                      </Header>
                      <List.List>
                      {courses.map((course: any) => (
                          <List.Item
                            key={course.id}>
                            <List.Content>
                              <List.Header as={Link} to={`/courses/${course.id}`}>
                                {course.name}
                              </List.Header>
                              <List.Description>
                      {course.SKU} <br/>
                                Attendance:{' '}
                                <PersonCourseAttendance
                                  personId={Number(id)}
                                  courseId={course.id}
                                />
                              </List.Description>
                            </List.Content>
                          </List.Item>
                        ))}
                      </List.List>
                    </List.Item>
                  ))}
              </List>
            )}
          </Segment>

          {/* --- Teaching Section --- */}
          {user.coursesTeaching.length > 0 && (
            <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
              <Header as="h2">
                <Icon name="graduation cap" /> Classes Teaching
              </Header>
              <Divider />
              <List divided relaxed>
                {Object.entries(
                  user.coursesTeaching.reduce((acc: any, c: any) => {
                    const sku = c.SKU.slice(-7);
                    if (!acc[sku]) {
                      acc[sku] = [];
                    }
                    acc[sku].push(c);
                    return acc;
                  }, {})
                )
                  .sort(
                    ([skuA], [skuB]) =>
                      Number(skuB.slice(-4)) - Number(skuA.slice(-4))
                  ) // Sort descending by year/SKU
                  .map(([sku, courses]: [string, any]) => (
                    <List.Item key={sku}>
                      <Header as="h4" style={{ marginBottom: '0.5em' }}>
                        {sku}
                      </Header>
                      <List.List>
                        {courses.map((course: any) => (
                          <List.Item key={course.id}>
                            <List.Content>
                              <List.Header as={Link} to={`/courses/${course.id}`}>
                                {course.name}
                              </List.Header>
                              <List.Description>
                                Teacher - {course.SKU}
                              </List.Description>
                            </List.Content>
                          </List.Item>
                        ))}
                      </List.List>
                    </List.Item>
                  ))}
              </List>
            </Segment>
          )}

          {/* --- Permissions Editor (if in edit mode) --- */}
          {canEdit && (
            <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
              <Header as="h2">
                <Icon name="lock" /> Permissions
              </Header>
              <Divider />
              <PermissionEditor
                userId={Number(id)}
                refetchPerson={() =>
                  refetch({ variables: { personId: Number(id) } })
                }
              />
            </Segment>
          )}

          {/* --- Archive Button (if in edit mode) --- */}
          {canEdit && (
            <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
              <Header as="h2" color="red">
                <Icon name="archive" /> Danger Zone
              </Header>
              <Divider />
              <Button color="red" onClick={archiveUser}>
                Archive Person
              </Button>
            </Segment>
          )}
        </Container>
      </Segment>
    </ResponsiveContainer>
  );
};

export { Profile };
